import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Card from 'ui/views/cards/Card';
import cx from 'classnames';
import LabelChip from 'ui/domain/Chips/LabelChip';
import Button from 'ui/elements/buttons/Button';
import Logo from 'ui/domain/Logo';
import { resize } from 'util/cloudinary';
import { staticFileLocations } from 'urls';
import { useTheme } from '@mui/material/styles';
import RoundedChip from 'ui/elements/Chip/RoundedChip';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import { stripHtml } from 'util/stringUtils';
import styles from './styles.scss';
import { PartnerOffer, PartnerOfferLayout } from 'domain/partnerOffers';
import { getLinkInfo } from './utils';
import CompanyProfileDialog from 'domain/companies/ProfileDialog/CompanyProfileDialog';
import SimpleLayout from './SimpleLayout';

export default function SelectedPartnerOffer(props: { offer: PartnerOffer; openInNewTab?: boolean }) {
  const { offer } = props;

  // If layout is Simple, render SimpleLayout component
  if (offer.layout === PartnerOfferLayout.Simple) {
    return <SimpleLayout offer={offer} openInNewTab={props.openInNewTab} />;
  }

  // Otherwise, render the default layout
  const theme = useTheme();
  const { resource: userProfile } = useSelfUserProfile();
  const [isCompanyDialogOpen, setIsCompanyDialogOpen] = useState(false);
  const linkBehaviour = props.openInNewTab
    ? {
        target: 'blank',
        rel: 'noopener noreferrer',
      }
    : null;

  const bannerHeight = 135;
  const logoHeight = 59;

  const backgroundImage = resize(offer.bannerUrl || staticFileLocations.defaultCompanyImage, { width: 'auto' });

  const userProfileData = userProfile.state === 'fetched' ? userProfile.resource : undefined;
  const linkInfo = getLinkInfo(offer, userProfileData);

  const commonStyles = {
    position: 'relative' as const,
  };
  const commonClassNames = cx('u-flex', 'u-flex--column', 'u-fullHeight');

  const cardContent = (
    <>
      <div
        style={{
          height: bannerHeight,
          background: offer.bannerUrl
            ? `url(${backgroundImage}) center / cover no-repeat`
            : theme.bluePlanetPalette.indigo.main,
          position: 'relative',
        }}
      >
        {offer.bannerLabel && (
          <div
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 1337,
            }}
          >
            <RoundedChip label={offer.bannerLabel.name} color={offer.bannerLabel.color} />
          </div>
        )}
      </div>
      <div
        className="u-content-padding-x"
        style={{
          position: 'absolute',
          zIndex: 2,
          top: bannerHeight - logoHeight / 2,
        }}
      >
        <Logo url={offer.logoUrl} border="2px solid white" type={'company'} size={logoHeight} />
      </div>

      <div className="u-content-padding-x u-section-padding-top">
        <div>
          <h3 className="text-h4">{offer.name}</h3>
          <p className={cx('text-body', styles.description)} style={{ whiteSpace: 'pre-line' }}>
            {stripHtml(offer.description ?? '')}
          </p>
        </div>
        <div>
          {offer.labels.length > 0 && (
            <div className="u-content-spacing-top">
              {offer.labels.map(label => (
                <LabelChip
                  key={label.name + label.color}
                  label={label.name}
                  color={label.color}
                  className={cx('u-quarter-spacing-right', 'u-quarter-spacing-bottom')}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={cx('u-content-padding', 'u-margin-top-auto')}>
        <Button kind="primary">{offer.linkText || 'Learn more'}</Button>
      </div>
    </>
  );

  return (
    <Card elevation={1} padding="none" hover="drop-shadow" className="u-fullHeight">
      {linkInfo.isCompany ? (
        <div
          onClick={() => setIsCompanyDialogOpen(true)}
          style={commonStyles}
          className={commonClassNames}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsCompanyDialogOpen(true);
            }
          }}
        >
          {cardContent}
        </div>
      ) : linkInfo.isInternal ? (
        <Link to={linkInfo.href} style={commonStyles} className={commonClassNames} {...linkBehaviour}>
          {cardContent}
        </Link>
      ) : (
        <a
          href={linkInfo.href}
          target="_blank"
          rel="noopener noreferrer"
          style={commonStyles}
          className={commonClassNames}
        >
          {cardContent}
        </a>
      )}
      {isCompanyDialogOpen && offer.companyId && (
        <CompanyProfileDialog
          companyId={offer.companyId}
          onClose={() => setIsCompanyDialogOpen(false)}
          analyticsCommunityId="none"
          analyticsContext="partner-offers"
        />
      )}
    </Card>
  );
}
